import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Audience, MessageDto, PreviewOptions, RecipientStatus, SendOptions } from '@/lib/types';

export const messageKeys = {
  all: (jobId: string) => ['jobs', jobId, 'messages'] as const,
  list: (jobId: string, params: { page?: number; pageSize?: number; status?: string; view?: 'upcoming' | 'sent' }) => ['jobs', jobId, 'messages', 'list', params] as const,
  detail: (jobId: string, messageId: string) => ['jobs', jobId, 'messages', 'detail', messageId] as const,
  recipients: (jobId: string, messageId: string, params: { page?: number; pageSize?: number; status?: RecipientStatus }) =>
    ['jobs', jobId, 'messages', 'detail', messageId, 'recipients', params] as const,
  recipient: (jobId: string, messageId: string, recipientId: string) => ['jobs', jobId, 'messages', 'detail', messageId, 'recipient', recipientId] as const,
  preview: (jobId: string, body: string, audience: Audience | undefined, options: PreviewOptions | undefined) => ['jobs', jobId, 'messages', 'preview', body, audience, options] as const,
};

const isInFlight = (m: MessageDto | undefined) => !!m && (m.status === 'queued' || m.status === 'sending');

export function useMessages(jobId: string, params: { page?: number; pageSize?: number; status?: string; view?: 'upcoming' | 'sent' }) {
  return useQuery({
    queryKey: messageKeys.list(jobId, params),
    queryFn: () => api.messages.list(jobId, params),
    placeholderData: keepPreviousData,
    refetchInterval: (q) => (q.state.data?.items.some(isInFlight) ? 5000 : false),
  });
}

export function useMessage(jobId: string, messageId: string) {
  return useQuery({
    queryKey: messageKeys.detail(jobId, messageId),
    queryFn: () => api.messages.get(jobId, messageId),
    enabled: !!messageId,
    refetchInterval: (q) => (isInFlight(q.state.data?.message) ? 5000 : false),
  });
}

export function useRecipients(jobId: string, messageId: string, params: { page?: number; pageSize?: number; status?: RecipientStatus }, live: boolean) {
  return useQuery({
    queryKey: messageKeys.recipients(jobId, messageId, params),
    queryFn: () => api.messages.recipients(jobId, messageId, params),
    enabled: !!messageId,
    placeholderData: keepPreviousData,
    refetchInterval: live ? 5000 : false,
  });
}

export function useRecipient(jobId: string, messageId: string, recipientId: string) {
  return useQuery({
    queryKey: messageKeys.recipient(jobId, messageId, recipientId),
    queryFn: () => api.messages.recipient(jobId, messageId, recipientId),
    enabled: !!messageId && !!recipientId,
    refetchInterval: (q) => {
      const s = q.state.data?.recipient.status;
      return s === 'queued' || s === 'sending' || s === 'sent' ? 5000 : false;
    },
  });
}

export function usePreview(jobId: string, body: string, audience: Audience | undefined, options?: PreviewOptions, enabled = true) {
  return useQuery({
    queryKey: messageKeys.preview(jobId, body, audience, options),
    queryFn: ({ signal }) => api.messages.preview(jobId, body, audience, options, signal),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

function useInvalidateMessages(jobId: string) {
  const qc = useQueryClient();
  return () => Promise.all([qc.invalidateQueries({ queryKey: messageKeys.all(jobId) }), qc.invalidateQueries({ queryKey: ['jobs', jobId, 'reports'] }), qc.invalidateQueries({ queryKey: ['jobs', 'detail', jobId] })]);
}

export function useSendMessage(jobId: string) {
  const invalidate = useInvalidateMessages(jobId);
  return useMutation({
    mutationFn: ({ body, audience, options }: { body: string; audience: Audience; options?: SendOptions }) => api.messages.send(jobId, body, audience, options),
    onSuccess: () => invalidate(),
  });
}

/** Send a draft or scheduled message right now. */
export function useSendNow(jobId: string) {
  const invalidate = useInvalidateMessages(jobId);
  return useMutation({ mutationFn: ({ messageId, bypassQuietHours }: { messageId: string; bypassQuietHours?: boolean }) => api.messages.sendNow(jobId, messageId, bypassQuietHours), onSuccess: () => invalidate() });
}

export function useCancelMessage(jobId: string) {
  const invalidate = useInvalidateMessages(jobId);
  return useMutation({ mutationFn: (messageId: string) => api.messages.cancel(jobId, messageId), onSuccess: () => invalidate() });
}

export function useUpdateDraft(jobId: string) {
  const invalidate = useInvalidateMessages(jobId);
  return useMutation({
    mutationFn: ({ messageId, body }: { messageId: string; body: { body?: string; audience?: Audience; translate?: boolean; translationOverrides?: Record<string, string>; scheduledAt?: string | null } }) => api.messages.update(jobId, messageId, body),
    onSuccess: () => invalidate(),
  });
}
